import type { Look } from './look.js';

/**
 * Staff NPCs: non-player characters a room owner can buy and place, same as
 * any other furniture (see `furniture/staff.ts`). Unlike Bot Kaki, each one
 * is owned by a player and only wanders near the spot it was placed.
 */
export type StaffNpcKind = 'bodyguard' | 'bar_manager' | 'lifeguard' | 'stadium_coach';

/** Avatar ids of staff NPCs start with this; real user ids never do. */
export const STAFF_NPC_ID_PREFIX = 'staff:';

export const STAFF_NPC_NAMES: Readonly<Record<StaffNpcKind, string>> = Object.freeze({
  bodyguard: 'Bruno',
  bar_manager: 'Marco',
  lifeguard: 'Nikki',
  stadium_coach: 'Coach Reed',
});

/** Outfit tweaks so each staff NPC reads as its own character, reusing the normal avatar renderer. */
export const STAFF_NPC_LOOKS: Readonly<Record<StaffNpcKind, Partial<Look>>> = Object.freeze({
  /** Office Shirt + Black + Shades: reads as plainclothes security. */
  bodyguard: { top: 3, topColor: 23, glasses: 2 },
  /** Apron over a shirt, for someone running the bar. */
  bar_manager: { top: 7, topColor: 13 },
  /** Singlet + Shorts + Straw Hat: beachwear with sun protection. */
  lifeguard: { top: 1, topColor: 5, bottom: 1, hat: 3 },
  /** Jersey + Cargo Pants + Cap: sporty coach gear. */
  stadium_coach: { top: 6, topColor: 8, bottom: 4, hat: 1 },
});

/** Ambient lines a staff NPC says now and then while someone is nearby. */
export const STAFF_NPC_LINES: Readonly<Record<StaffNpcKind, readonly string[]>> = Object.freeze({
  bodyguard: ["Move along, nothing to see here.", "I've got my eye on things.", 'Stay safe in here.'],
  bar_manager: ['What are you drinking tonight?', 'Let me know if you need a refill.', 'Cheers!'],
  lifeguard: ['No diving please!', 'Swim safe out there.', "I'm watching the water, don't worry."],
  stadium_coach: ['Faster! Stronger! Let\'s go!', 'Nice form today.', 'Who wants to race?'],
});

/** First-person blurb for the infostand card when a player clicks a staff NPC. */
export const STAFF_NPC_ROLES: Readonly<Record<StaffNpcKind, string>> = Object.freeze({
  bodyguard: "I'm hired muscle for this room -- purely for show, I just look intimidating.",
  bar_manager: "I keep an eye on the counter here and like to chat with whoever drops by.",
  lifeguard: "I patrol the poolside, whistle at the ready. No diving on my watch!",
  stadium_coach: "I pace the track and cheer on anyone who runs past. Let's go!",
});

/** How close (tiles) counts as "near enough" for a staff NPC to notice someone and speak. */
export const STAFF_NPC_NOTICE_RANGE = 2;
/** How far (tiles) a staff NPC will wander from the spot it was placed. */
export const STAFF_NPC_LEASH_TILES = 3;

const NAME_TO_KIND: Readonly<Record<string, StaffNpcKind>> = Object.freeze(
  Object.fromEntries(Object.entries(STAFF_NPC_NAMES).map(([kind, name]) => [name, kind as StaffNpcKind])),
);

/** True for avatar ids spawned by `addStaffNpc` (see `STAFF_NPC_ID_PREFIX`); real user ids never match. */
export function isStaffNpcId(id: string): boolean {
  return id.startsWith(STAFF_NPC_ID_PREFIX);
}

/** Recovers which staff role an NPC is from its (fixed, unique) display name. */
export function staffNpcKindByName(name: string): StaffNpcKind | null {
  return NAME_TO_KIND[name] ?? null;
}
